import moment from "moment";
import { differenceInDays } from "date-fns";

export function getTimelinePosition(
  startDate,
  endDate,
  viewType,
  currentDate,
  displayYearRange,
  dynamicWidths = null
) {
  // Normalize all inputs to moment (date-only, no time component)
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");
  const current = moment(currentDate).startOf("day");

  if (!start.isValid() || !end.isValid() || !current.isValid()) {
    return { left: 0, width: 0, shouldRender: false };
  }

  const { start: displayStartYear, end: displayEndYear } = displayYearRange;
  const startYear = start.year();
  const endYear = end.year();

  // Check if the date range overlaps with the display range
  if (startYear > displayEndYear || endYear < displayStartYear) {
    return { left: 0, width: 0, shouldRender: false };
  }

  // Clamp dates to the display range for calculations
  const displayStart = moment(`${displayStartYear}-01-01`).startOf("day");
  const displayEnd = moment(`${displayEndYear}-12-31`).startOf("day");
  const clampedStart = moment.max(start, displayStart);
  const clampedEnd = moment.min(end, displayEnd);

  // Use dynamic widths if available, otherwise use fixed widths
  const getQuarterWidth = () => {
    return dynamicWidths?.useDynamic ? dynamicWidths.quarterWidth : 125.25;
  };

  switch (viewType.toLowerCase()) {
    case "monthly":
    case "month": {
      const monthWidth = 241;

      // Calculate total months in display range
      const totalMonths = (displayEndYear - displayStartYear + 1) * 12;

      // Calculate start position
      const monthsSinceDisplayStart =
        (clampedStart.year() - displayStartYear) * 12 + clampedStart.month();

      const startDay = clampedStart.date();
      const daysInStartMonth = clampedStart.daysInMonth();

      const left =
        monthsSinceDisplayStart * monthWidth + (startDay - 1) * (monthWidth / daysInStartMonth);

      // Handle single day events
      if (clampedStart.isSame(clampedEnd, "day")) {
        const dayWidth = monthWidth / daysInStartMonth;
        return {
          left: left + 4,
          width: Math.max(dayWidth / 3, 15),
          shouldRender: true,
        };
      }

      // Calculate width for multi-day events
      const daysDiff = clampedEnd.diff(clampedStart, "days") + 1;
      const daysInEndMonth = clampedEnd.daysInMonth();

      let width = 0;
      if (clampedStart.month() === clampedEnd.month() && clampedStart.year() === clampedEnd.year()) {
        width = daysDiff * (monthWidth / daysInStartMonth);
      } else {
        // Width for first month
        const daysInFirstMonth = daysInStartMonth - startDay + 1;
        const firstMonthWidth = daysInFirstMonth * (monthWidth / daysInStartMonth);

        // Remaining days approximate width using end month day width
        const remainingDays = daysDiff - daysInFirstMonth;
        width = firstMonthWidth + remainingDays * (monthWidth / daysInEndMonth);
      }

      return { left: left + 4, width: width - 2, shouldRender: true };
    }

    case "weekly":
    case "week": {
      const weekWidth = 241;
      const dayWidth = weekWidth / 7;

      let firstMondayOfDisplayRange = moment(`${displayStartYear}-01-01`).startOf("day");
      while (firstMondayOfDisplayRange.day() !== 1) {
        firstMondayOfDisplayRange.subtract(1, "day");
      }

      const startDaysSinceFirstMonday = clampedStart.diff(firstMondayOfDisplayRange, "days");
      const endDaysSinceFirstMonday = clampedEnd.diff(firstMondayOfDisplayRange, "days");

      const startWeekIndex = Math.floor(startDaysSinceFirstMonday / 7);
      const endWeekIndex = Math.floor(endDaysSinceFirstMonday / 7);

      const startDayInWeek = startDaysSinceFirstMonday % 7;
      const endDayInWeek = endDaysSinceFirstMonday % 7;

      const left = startWeekIndex * weekWidth + startDayInWeek * dayWidth;
      let width;

      if (startWeekIndex === endWeekIndex) {
        width = (endDayInWeek - startDayInWeek + 1) * dayWidth;
      } else {
        const completeWeeks = endWeekIndex - startWeekIndex - 1;
        const daysInFirstWeek = 7 - startDayInWeek;
        const daysInLastWeek = endDayInWeek + 1;
        width = daysInFirstWeek * dayWidth + completeWeeks * weekWidth + daysInLastWeek * dayWidth;
      }

      return { left: left + 4, width: width - 3, shouldRender: true };
    }

    case "daily":
    case "day": {
      const dayWidth = 241;
      const firstDayOfDisplayRange = moment(`${displayStartYear}-01-01`).startOf("day");

      const startDayIndex = clampedStart.diff(firstDayOfDisplayRange, "days");
      const endDayIndex = clampedEnd.diff(firstDayOfDisplayRange, "days");

      const left = startDayIndex * dayWidth + 4;
      const width = (endDayIndex - startDayIndex + 1) * dayWidth - 3;

      return { left, width, shouldRender: true };
    }

    case "quarter": {
      const quarterWidth = 501;

      const startQuarter = Math.floor(clampedStart.month() / 3);
      const endQuarter = Math.floor(clampedEnd.month() / 3);

      // Start position: quarters since display start year + offset inside quarter
      const quartersSinceDisplayStart =
        (clampedStart.year() - displayStartYear) * 4 + startQuarter;

      // Calculate offset inside quarter
      const monthInQuarter = clampedStart.month() % 3;
      const daysInQuarterStartMonth = clampedStart.daysInMonth();
      const offsetInsideQuarter = monthInQuarter * (quarterWidth / 3) +
        ((clampedStart.date() - 1) * ((quarterWidth / 3) / daysInQuarterStartMonth));

      const left = quartersSinceDisplayStart * quarterWidth + offsetInsideQuarter + 4;

      // Calculate width
      const totalDays = clampedEnd.diff(clampedStart, "days") + 1;
      const startMonthDays = clampedStart.daysInMonth();
      const approxDayWidth = quarterWidth / (3 * startMonthDays); // approximate per-day width

      const width = Math.max(totalDays * approxDayWidth, 15) - 3;

      return { left, width , shouldRender: true };
    }

    case "year": {
      const startYear = displayYearRange.start;
      const endYear = displayYearRange.end;
      const quarterWidth = getQuarterWidth();
      const yearWidth = quarterWidth * 4;
      
      const rangeStart = moment(`${startYear}-01-01`).startOf("day");
      const rangeEnd = moment(`${endYear}-12-31`).startOf("day");
      
      // Calculate which year and quarter the start date falls into
      const yearsFromStart = clampedStart.year() - startYear;
      const quarterInYear = Math.floor(clampedStart.month() / 3);
      
      // Position = (years from start × yearWidth) + (quarter in year × quarterWidth) + offset in quarter
      const yearPosition = yearsFromStart * yearWidth;
      const quarterPosition = quarterInYear * quarterWidth;
      
      // Calculate offset within the quarter (same logic as quarter view)
      const monthInQuarter = clampedStart.month() % 3;
      const daysInStartMonth = clampedStart.daysInMonth();
      const dayOfMonth = clampedStart.date();
      const monthWidth = quarterWidth / 3; // 167px per month
      
      const offsetInsideQuarter = 
        monthInQuarter * monthWidth + 
        ((dayOfMonth - 1) / daysInStartMonth) * monthWidth;
      
      const left = yearPosition + quarterPosition + offsetInsideQuarter;
      
      // Calculate width (same as quarter view logic)
      const totalDays = clampedEnd.diff(clampedStart, 'days') + 1;
      
      let width = 0;
      if (clampedStart.year() === clampedEnd.year() && clampedStart.month() === clampedEnd.month()) {
        // Same month
        width = (totalDays / daysInStartMonth) * monthWidth;
      } else {
        // Different months - calculate proportionally
        const daysInEndMonth = clampedEnd.daysInMonth();
        const avgDaysPerMonth = (daysInStartMonth + daysInEndMonth) / 2;
        width = (totalDays / avgDaysPerMonth) * monthWidth;
      }
      
      const shouldRender = clampedEnd.isSameOrAfter(rangeStart) && clampedStart.isSameOrBefore(rangeEnd);
      
      return {
        left: Math.max(0, left + 4),
        width: Math.max(15, width - 3),
        shouldRender,
      };
    }

    default:
      return { left: 0, width: 0, shouldRender: false };
  }
}
